
import i18next from 'i18next';
import { PureComponent } from 'react';

import { getLocalParticipant } from '../../base/participants';
import { shouldDisplayTileView } from '../../video-layout';

/**
 * {@code AbstractCaptions} Properties.
 */
export type AbstractCaptionsProps = {

        /**
     * Whether local participant is displaying subtitles.
     */
        _displaySubtitles: boolean;

    /**
     * Whether local participant is requesting to see subtitles.
     */
    _requestingSubtitles: boolean,

    /**
     * Transcript texts formatted with participant's name and final content.
     * Mapped by id just to have the keys for convenience during the rendering
     * process.
     */
    _transcripts: ?Map<string, string>,

    /**
     * Full raw transcript messages containing participant details.
     */
    _transcriptMessages?: ?Map<string, Object>,

    /**
     * Current local participant object.
     */
    _localParticipant?: ?Object
};

/**
 * Abstract React {@code Component} which can display speech-to-text results
 * from Jigasi as subtitles.
 */
export class AbstractCaptions<P: AbstractCaptionsProps>
    extends PureComponent<P> {

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {React$Element}
     */
    render() {
        const { _requestingSubtitles, _transcripts } = this.props;

        if (!_requestingSubtitles || !_transcripts || !_transcripts.size) {
            return null;
        }

        const paragraphs = [];

        for (const [ id, text ] of _transcripts) {
            paragraphs.push(this._renderParagraph(id, text));
        }

        return this._renderSubtitlesContainer(paragraphs);
    }

    /**
     * Renders the transcription text.
     *
     * @abstract
     * @param {string} id - The ID of the transcript message from which the
     * {@code text} has been created.
     * @param {string} text - Subtitles text formatted with the participant's
     * name.
     * @protected
     * @returns {React$Element} - The React element which displays the text.
     */
    _renderParagraph: (id: string, text: string) => React$Element<*>;

    /**
     * Renders the subtitles container.
     *
     * @abstract
     * @param {Array<React$Element>} paragraphs - An array of elements created
     * for each subtitle using the {@link _renderParagraph} method.
     * @protected
     * @returns {React$Element} - The subtitles container.
     */
    _renderSubtitlesContainer: (Array<React$Element<*>>) => React$Element<*>;
}

/**
 * Formats the transcript messages into text by prefixing participant's name to
 * avoid duplicating the effort on platform specific component.
 *
 * @param {Object} state - The redux state.
 * @private
 * @returns {Map<string, string>} - Formatted transcript subtitles mapped by
 * transcript message IDs.
 */
function _constructTranscripts(state: Object): Map<string, string> {
    const { _transcriptMessages } = state['features/subtitles'];

    const transcripts = new Map();

    for (const [ id, transcriptMessage ] of _transcriptMessages) {
        if (transcriptMessage) {
            let text = `${transcriptMessage.participant.name}: `;
            const original = transcriptMessage.final
                || `${transcriptMessage.stable || ''}${transcriptMessage.unstable || ''}`;
            const { translated } = transcriptMessage;

            // Only the listener's language once the translation arrives. Until then - or when the sentence was
            // already in that language or could not be translated - what was actually said.
            text += translated || original;

            transcripts.set(id, `${text}`);
        }
    }

    return transcripts;
}

/**
 * Maps the transcriptionSubtitles in the redux state to the associated props of
 * {@code AbstractCaptions}.
 *
 * @param {Object} state - The redux state.
 * @private
 * @returns {{
 *     _requestingSubtitles: boolean,
 *     _transcripts: Map<string, string>
 * }}
 */
export function _abstractMapStateToProps(state: Object) {
    const { _requestingSubtitles, _displaySubtitles, _requestingCaptionsModal, _language, _transcriptMessages } = state['features/subtitles'];
    const { transcription, iAmRecorder } = state['features/base/config'];
    const OngoingTranscriptionLanguage = state['features/base/conference']?.conference?.options?.config?.transcriptionLanguage;
    const translationEnabled = transcription?.translationEnabled;
    const tileviewStatus = shouldDisplayTileView(state);
    const transcripts = _constructTranscripts(state);
    const { language: currentLanguage } = i18next;
    const { confSettings, meetingDetails } = state['features/base/jwt'] || {};
    const settings = confSettings || meetingDetails?.settings;
    const _localParticipant = getLocalParticipant(state);

    return {
        _displaySubtitles,
        _requestingSubtitles,
        _requestingCaptionsModal,
        OngoingTranscriptionLanguage,
        translationEnabled,
        iAmRecorder,
        tileviewStatus,
        transcription,
        confSettings: settings,
        _localParticipant,
        _transcriptMessages,

        // avoid re-renders by setting to prop new empty Map instances.
        _transcripts: transcripts.size === 0 ? undefined : transcripts,
        _currentLanguage: currentLanguage,
        _translationEnabled: transcription?.translationEnabled,
        _language };
}
